import { __assign } from 'tslib';
import React__default, { useState, useEffect } from 'react';
import { KpkFilterModifierType } from '@keepeek/commons';
import { Slider } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import FilterWrapper from '../FilterWrapper/index.js';

// 7 days
var MAX_REAL_VALUE = 60 * 60 * 24 * 7;
// Use a stepper of 60 for "easy" calculation in second
var markStepper = 60;
// Create marks with stepper
// Marks only purpose is to show a dot on the slider to have a visual feedback of the scale
// For "easy" comprehension, we use a scale of 60 (60s in a minute) so the first mark is on point (value = value)
// The other marks are in minute, so we use 60 step for every gap between two mark
// This way we have a slider evenly distributed, but with different scale possible
// ex: the gap between the marks()[1] and marks()[2] is 5 minute, but in a step of 60, so 60 = 1min and 120 = 5min.
// The last mark should return 1hour and above (7days), so we just fill the gap between the last and the previous in a step of 60
// ex: the gap between the marks()[3] and marks()[4] is 50 minute, but in a step of 60, so 180 = 10min and 240 = 60min|1h.
var marks = function (t) { return [
    {
        // 0 second
        value: 0,
        label: t ? t("components.filter-duration.second-unit.label", { value: 0 }) : "",
        realValueInSeconds: 0
    },
    {
        // 1 minute
        value: markStepper,
        label: t ? t("components.filter-duration.minute-unit.label", { value: 1 }) : "",
        realValueInSeconds: markStepper
    },
    {
        // 5 minutes
        value: 2 * markStepper,
        label: t ? t("components.filter-duration.minute-unit.label", { value: 5 }) : "",
        realValueInSeconds: 5 * markStepper
    },
    {
        // 10 minutes
        value: 3 * markStepper,
        label: t ? t("components.filter-duration.minute-unit.label", { value: 10 }) : "",
        realValueInSeconds: 10 * markStepper
    },
    {
        // 1hour
        value: 4 * markStepper,
        label: t ? t("components.filter-duration.max-unit.label") : "",
        realValueInSeconds: 60 * markStepper
    },
]; };
var marksLength = marks().length;
// Calc the qty of minutes between two mark above mark in seconds
var minutesBetweenTwoMarks = (marks()[2].realValueInSeconds - marks()[1].realValueInSeconds) / markStepper + 1;
// Calc the number of steps between two marks in minute but with the same scale as seconds
var markMinuteStepper = markStepper / minutesBetweenTwoMarks;
// Take the first and last for calculation
var firstMarkValue = marks()[0].value;
var lastMarkValue = marks()[marks().length - 1].value;
// Take last and previous for gap calculation
var gapBetweenLastAndPreviousInSeconds = marks()[marksLength - 1].realValueInSeconds - marks()[marksLength - 2].realValueInSeconds;
var gapBetweenLastAndPreviousInMinute = Math.ceil(gapBetweenLastAndPreviousInSeconds / markStepper);
// Take previous last for offest
var offsetPreviousLastInSeconds = marks()[marksLength - 2].realValueInSeconds;
var offsetPreviousLastInMinute = Math.floor(offsetPreviousLastInSeconds / markStepper) - 1;
function valueLabelFormat(value, t) {
    if (value === lastMarkValue) {
        // We're hitting max value of the slider
        return t("components.filter-duration.short-max-unit.label");
    }
    else if (value < markStepper) {
        // We're under a minute
        return t("components.filter-duration.short-second-unit.label", { value: value });
    }
    else if (value < markStepper * (marksLength - 2)) {
        // We're on minute step but under last mark and previous
        var minute = (value - markStepper) / markMinuteStepper + 1;
        var valueCalc = Math.floor(minute);
        return t("components.filter-duration.short-minute-unit.label", {
            value: valueCalc
        });
    }
    else {
        var minute = value - markStepper * (marksLength - 2) === 0 ? 1 : value - markStepper * (marksLength - 2);
        var valueCalc = Math.ceil((minute / markStepper) * gapBetweenLastAndPreviousInMinute) +
            offsetPreviousLastInMinute;
        return t("components.filter-duration.short-minute-unit.label", {
            value: valueCalc
        });
    }
}
// We need to adapt the value received to match our marks and custom scale
// because the filter work in second and the slider work in step of 60 no matter what time is in between
// So we receveive a value in second and need to convert it in mark step
function adaptForInFilter(value, unit) {
    var adaptedValue = value;
    if (unit === "m") {
        adaptedValue = adaptedValue * markStepper;
    }
    // If we received the max value then just hit the last
    if (adaptedValue === MAX_REAL_VALUE) {
        return lastMarkValue;
    }
    else {
        if (adaptedValue <= markStepper) {
            // no adaptation needed, we're in the good scale in seconds
            return adaptedValue;
        }
        else if (adaptedValue < marks()[marksLength - 2].realValueInSeconds) {
            // adaptation needed from 1min to 10min as we know from marks
            // Convert seconds in minute
            var minutes = Math.floor(adaptedValue / markStepper);
            // convert to marks scale
            // add markStepper to have the first mark skipped
            // add markMinuteStepper minus 1 to be on the far right of the slider
            return (minutes - 1) * markMinuteStepper + markStepper + markMinuteStepper - 1;
        }
        else {
            // Convert seconds in minute
            var minutes = Math.ceil(adaptedValue / markStepper);
            // convert to marks scale
            // There's 50 min between these two marks, so 10 * more than between the previous => divide by 10
            var converted = minutes * (markMinuteStepper / 10) +
                3 * markStepper -
                (markStepper / gapBetweenLastAndPreviousInMinute) * offsetPreviousLastInMinute;
            return converted;
        }
    }
}
// Should return value in second adapted from mark scale
function adaptForOutFilter(value) {
    if (value === lastMarkValue) {
        // We're hitting max value of the slider
        return MAX_REAL_VALUE;
    }
    else if (value < markStepper) {
        // We're under a minute
        return value;
    }
    else if (value < markStepper * (marksLength - 2)) {
        // We're on minute step but under last mark and previous
        var minute = (value - markStepper) / markMinuteStepper + 1;
        return Math.floor(minute);
    }
    else {
        var minute = value - markStepper * (marksLength - 2) === 0 ? 1 : value - markStepper * (marksLength - 2);
        return (Math.ceil((minute / markStepper) * gapBetweenLastAndPreviousInMinute) +
            offsetPreviousLastInMinute);
    }
}
var FilterDuration = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    var filter = _a.filter, className = _a.className, onFilterUpdate = _a.onFilterUpdate, expanded = _a.expanded, expandIcon = _a.expandIcon, setExpanded = _a.setExpanded, sx = _a.sx, KIcon = _a.KIcon;
    var t = useTranslation("refront-components").t;
    var fromDurationSecondInitValue = adaptForInFilter((_d = (_c = (_b = filter === null || filter === void 0 ? void 0 : filter.values) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : 0, ((_g = (_f = (_e = filter === null || filter === void 0 ? void 0 : filter.values) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.label) === null || _g === void 0 ? void 0 : _g.includes("m")) ? "m" : "s");
    var toDurationSecondInitValue = adaptForInFilter((_k = (_j = (_h = filter === null || filter === void 0 ? void 0 : filter.values) === null || _h === void 0 ? void 0 : _h[1]) === null || _j === void 0 ? void 0 : _j.id) !== null && _k !== void 0 ? _k : 0, ((_o = (_m = (_l = filter === null || filter === void 0 ? void 0 : filter.values) === null || _l === void 0 ? void 0 : _l[1]) === null || _m === void 0 ? void 0 : _m.label) === null || _o === void 0 ? void 0 : _o.includes("m")) ? "m" : "s");
    var _p = useState([
        fromDurationSecondInitValue,
        toDurationSecondInitValue,
    ]), duration = _p[0], setDuration = _p[1];
    // Need to use useEffect here to change state if derived prop state change :
    // https://reacthustle.com/blog/how-to-update-state-when-props-change-in-react
    useEffect(function () { return setDuration([fromDurationSecondInitValue, toDurationSecondInitValue]); }, [fromDurationSecondInitValue, toDurationSecondInitValue]);
    function handleChange(_, value) {
        var from = (Array.isArray(value) && value[0]) || 0;
        var to = (Array.isArray(value) && value[1]) || 0;
        setDuration([from, to]);
    }
    function handleChangeCommitted(_, value) {
        var _a;
        var from = (Array.isArray(value) && value[0]) || 0;
        var to = (Array.isArray(value) && value[1]) || 0;
        onFilterUpdate(__assign(__assign({}, filter), { modifiers: ((_a = filter.modifiers) === null || _a === void 0 ? void 0 : _a.map(function (m) {
                return __assign(__assign({}, m), { selected: m.type === KpkFilterModifierType.FromTo });
            })) || null, values: [
                {
                    id: adaptForOutFilter(from),
                    label: "".concat(valueLabelFormat(from, t)),
                    selected: null
                },
                {
                    id: adaptForOutFilter(to),
                    label: "".concat(valueLabelFormat(to, t)),
                    selected: null
                },
            ] }));
    }
    return (React__default.createElement(FilterWrapper, { className: clsx("FilterDuration", className), filter: filter, expanded: expanded, setExpanded: setExpanded, expandIcon: expandIcon, testId: "FilterDuration", sx: sx, KIcon: KIcon },
        React__default.createElement(Slider, { value: duration, onChange: handleChange, onChangeCommitted: handleChangeCommitted, "aria-labelledby": "range-slider", valueLabelDisplay: "on", marks: marks(t), step: 1, min: firstMarkValue, max: lastMarkValue, valueLabelFormat: function (value) { return valueLabelFormat(value, t); }, sx: {
                "& .MuiSlider-valueLabel": {
                    backgroundColor: function (theme) { return theme.palette.primary.main; }
                }
            }, size: "small" })));
};

export { MAX_REAL_VALUE, FilterDuration as default };
//# sourceMappingURL=index.js.map
